/* MS Belts 3.2, belt builder and mockup gallery. Loaded with defer by sections/belt-builder.liquid and
   sections/mockup-gallery.liquid (the guard below makes a second copy on the same page a no-op).
   <belt-builder>   live drawing (snippets/belt-svg.liquid), live price (variant matrix + add-ons + volume tier), build
                    summary, add to cart, quote text.
   <mockup-gallery> lightbox for the gallery tiles (native <dialog>: focus trap, Escape, inert page).
   No dependencies, no innerHTML with user input. Cart: window.MS (assets/ms.js). Analytics: window.MSTrack. */
(function () {
  'use strict';

  if (window.MSBuilder) return;
  window.MSBuilder = { ready: true };

  var doc = document;
  var GROUP_TITLE = 'Custom championship belt';
  var TEXT_MAX = 252; /* width of the name plate text, in drawing units */

  function track(name, params) {
    try {
      if (window.MSTrack && typeof window.MSTrack.event === 'function') window.MSTrack.event(name, params || {});
    } catch (error) {
      /* Tracking must never break the page. */
    }
  }

  function readConfig() {
    var element = doc.getElementById('ms-config');
    try {
      return element ? JSON.parse(element.textContent) || {} : {};
    } catch (error) {
      return {};
    }
  }

  function rootUrl() {
    var config = readConfig();
    var root = (config.routes && config.routes.root) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    root = String(root);
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /* Formats cents with the shop money format, for example "${{amount}}". Tags in the format are dropped. */
  function formatMoney(cents, format) {
    format = String(format || '').replace(/<[^>]*>/g, '');
    function number(decimals, thousands, decimal) {
      var parts = (Math.abs(cents) / 100).toFixed(decimals).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var pattern = /\{\{\s*(\w+)\s*\}\}/;
    if (!pattern.test(format)) format = '${{amount}}';
    return format.replace(pattern, function (match, key) {
      switch (key) {
        case 'amount_no_decimals': return number(0, ',', '.');
        case 'amount_with_comma_separator': return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(0, '.', ',');
        case 'amount_with_apostrophe_separator': return number(2, '\'', '.');
        case 'amount_no_decimals_with_space_separator': return number(0, ' ', ',');
        case 'amount_with_space_separator': return number(2, ' ', ',');
        case 'amount_with_period_and_space_separator': return number(2, ' ', '.');
        default: return number(2, ',', '.');
      }
    });
  }

  function groupId() {
    var part = Math.random().toString(36).slice(2, 8);
    return 'b' + Date.now().toString(36).slice(-4) + part;
  }

  /* Squeezes a line of drawing text into the name plate when it is too wide. */
  function fitText(text, max) {
    if (!text || typeof text.getComputedTextLength !== 'function') return;
    text.removeAttribute('textLength');
    text.removeAttribute('lengthAdjust');
    var width = 0;
    try {
      width = text.getComputedTextLength();
    } catch (error) {
      width = 0;
    }
    if (width > max) {
      text.setAttribute('textLength', String(max));
      text.setAttribute('lengthAdjust', 'spacingAndGlyphs');
    }
  }

  function fitDrawing(svg) {
    if (!svg) return;
    fitText(svg.querySelector('.belt-svg__l1'), TEXT_MAX);
    fitText(svg.querySelector('.belt-svg__l2'), TEXT_MAX);
  }

  function whenFontsReady(callback) {
    if (doc.fonts && doc.fonts.ready && typeof doc.fonts.ready.then === 'function') {
      doc.fonts.ready.then(callback, callback);
    }
  }

  function toResult(response) {
    return response.text().then(function (text) {
      var data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        data = {};
      }
      return { ok: response.ok, status: response.status, data: data || {} };
    });
  }

  function element(tag, className, text) {
    var node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function row(label, value, className) {
    var wrap = element('div', 'belt-builder__row' + (className ? ' ' + className : ''));
    wrap.appendChild(element('dt', '', label));
    var dd = element('dd', '', value);
    wrap.appendChild(dd);
    return wrap;
  }

  var SHAPE_WORDS = { classic: 'classic', shield: 'shield-shaped', winged: 'winged', round: 'round', rectangle: 'rectangular' };

  /* ---------- <belt-builder> ---------- */
  var THICK_DRAW = { 2: '2', 3: '4', 4: '6', 5: '8' }; /* drawing steps in belt-svg.liquid */

  class BeltBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.setup();
    }

    setup() {
      var self = this;
      this.config = readConfig();
      this.format = this.config.moneyFormat || '${{amount}}';
      this.svg = this.querySelector('.belt-svg');
      this.base = {
        variant: Number(this.getAttribute('data-base-variant')) || 0,
        price: Number(this.getAttribute('data-base-price')) || 0,
        title: this.getAttribute('data-base-title') || GROUP_TITLE
      };
      this.direct = this.getAttribute('data-direct') === 'true';
      this.quoteAbove = Number(this.getAttribute('data-quote-above')) || 10;
      this.optIndex = {
        build: Number(this.getAttribute('data-opt-build')) || 0,
        finish: Number(this.getAttribute('data-opt-finish')) || 0,
        thickness: Number(this.getAttribute('data-opt-thick')) || 0
      };
      this.optCount = Number(this.getAttribute('data-opt-count')) || 0;
      this.variants = {};
      var map = this.querySelector('[data-bb-variants]');
      if (map) {
        try {
          this.variants = JSON.parse(map.textContent) || {};
        } catch (error) {
          this.variants = {};
        }
      }
      this.engrave = this.querySelector('[data-bb-engrave]');
      this.line1 = this.querySelector('[data-bb-text="line1"]');
      this.line2 = this.querySelector('[data-bb-text="line2"]');
      this.logo = this.querySelector('[data-bb-logo]');
      this.notes = this.querySelector('[data-bb-notes]');
      this.summary = this.querySelector('[data-bb-summary]');
      this.prices = this.querySelector('[data-bb-prices]');
      this.totals = Array.prototype.slice.call(this.querySelectorAll('[data-bb-total], [data-bb-total-button]'));
      this.addButton = this.querySelector('[data-bb-add]');
      this.addWrap = this.addButton ? this.addButton.closest('.belt-builder__cart') : null;
      this.quoteNote = this.querySelector('[data-bb-quote-note]');
      this.error = this.querySelector('[data-bb-error]');
      this.quoteForm = this.querySelector('form.belt-builder__quote');
      this.quoteBox = this.querySelector('.belt-builder__quote-box');
      this.quoteBody = this.querySelector('[data-bb-quote-body]');
      this.refresh = this.querySelector('[data-bb-refresh]');
      this.timers = {};
      this.lastText = false;
      this.lastLogo = false;
      this.quoteDirty = !!(this.quoteBody && this.quoteBody.value.trim());
      this.quoteStarted = false;

      this.addEventListener('change', function (event) { self.onChange(event); });
      this.addEventListener('input', function (event) { self.onInput(event); });

      if (this.addButton) {
        this.addButton.addEventListener('click', function () { self.addToCart(self.addButton); });
      }
      if (this.quoteBody) {
        this.quoteBody.addEventListener('input', function () {
          self.quoteDirty = true;
          if (self.refresh) self.refresh.hidden = false;
        });
      }
      if (this.refresh) {
        this.refresh.hidden = !this.quoteDirty;
        this.refresh.addEventListener('click', function () {
          self.quoteDirty = false;
          self.refresh.hidden = true;
          self.writeQuote(self.state());
          if (self.quoteBody) self.quoteBody.focus();
        });
      }
      if (this.quoteForm) this.setupQuote();

      this.update();
      whenFontsReady(function () { fitDrawing(self.svg); });
    }

    /* ----- state ----- */
    option(name) {
      var inputs = this.querySelectorAll('[data-bb-option="' + name + '"]');
      var checked = null;
      for (var i = 0; i < inputs.length; i += 1) {
        if (inputs[i].checked) { checked = inputs[i]; break; }
      }
      if (!checked) checked = inputs[0] || null;
      if (!checked) return { el: null, value: '', label: '', variant: 0, price: 0, addon: '', diff: 0 };
      return {
        el: checked,
        value: checked.value,
        label: checked.getAttribute('data-bb-label') || checked.value,
        variant: Number(checked.getAttribute('data-bb-variant')) || 0,
        price: Number(checked.getAttribute('data-bb-price')) || 0,
        addon: checked.getAttribute('data-bb-addon') || '',
        diff: Number(checked.getAttribute('data-bb-diff-price')) || 0
      };
    }

    /* The base listing's variant for the chosen build, finish and thickness. */
    resolveVariant(build, finish, thickness) {
      if (!this.optCount) return null;
      var parts = [];
      for (var i = 0; i < this.optCount; i += 1) parts.push('');
      if (build.value) parts[this.optIndex.build] = build.value;
      if (finish.value) parts[this.optIndex.finish] = finish.value;
      if (thickness.value) parts[this.optIndex.thickness] = thickness.value;
      var found = this.variants[parts.join('|')];
      return found && found.id ? found : null;
    }

    state() {
      var line1 = this.line1 ? this.line1.value.replace(/\s+/g, ' ').trim().slice(0, 30) : '';
      var line2 = this.line2 ? this.line2.value.replace(/\s+/g, ' ').trim().slice(0, 30) : '';
      if (!line1 && line2) {
        line1 = line2;
        line2 = '';
      }
      var file = this.logo && this.logo.files && this.logo.files.length ? this.logo.files[0] : null;
      var engraving = null;
      if (this.engrave && line1) {
        engraving = {
          variant: Number(this.engrave.getAttribute('data-bb-variant')) || 0,
          price: Number(this.engrave.getAttribute('data-bb-price')) || 0,
          addon: 'Name plate engraving'
        };
      }
      var build = this.option('build');
      var finish = this.option('finish');
      var thickness = this.option('thickness');
      var speed = this.option('speed');
      var qtyPick = this.option('qty');
      var qty = qtyPick.el ? Number(qtyPick.el.getAttribute('data-bb-qty')) || 0 : 1;
      var pct = qtyPick.el ? Number(qtyPick.el.getAttribute('data-bb-pct')) || 0 : 0;
      var quoteOnly = qty < 1;
      var count = quoteOnly ? 1 : qty;
      var variant = this.resolveVariant(build, finish, thickness);
      var unit = variant ? variant.price : this.base.price + build.diff + finish.diff + thickness.diff;

      var state = {
        shape: this.option('shape'),
        sides: this.option('sides'),
        build: build,
        finish: finish,
        thickness: thickness,
        strap: this.option('strap'),
        speed: speed,
        qty: qty,
        qtyLabel: qtyPick.label || '1 belt',
        pct: pct,
        quoteOnly: quoteOnly,
        count: count,
        variant: variant,
        unit: unit,
        line1: line1,
        line2: line2,
        engraving: engraving,
        file: file,
        notes: this.notes ? this.notes.value.trim().slice(0, 1000) : ''
      };
      state.addons = [];
      if (speed.variant) state.addons.push(speed);
      if (engraving && engraving.variant) state.addons.push(engraving);
      state.discount = pct ? Math.round(unit * pct / 100) : 0;
      state.perBelt = unit - state.discount + state.addons.reduce(function (sum, choice) { return sum + choice.price; }, 0);
      state.total = state.perBelt * count;
      return state;
    }

    money(cents) {
      return formatMoney(cents, this.format);
    }

    buildLine(state) {
      var parts = [];
      if (state.build.label) parts.push(state.build.label);
      if (state.finish.label) parts.push(state.finish.label + ' finish');
      if (state.thickness.label) parts.push(state.thickness.label + ' plates');
      return parts.length ? parts.join(', ') : 'Standard build';
    }

    textLine(state) {
      if (!state.line1) return 'No text';
      return state.line2 ? state.line1 + ' / ' + state.line2 : state.line1;
    }

    /* ----- events ----- */
    onChange(event) {
      var target = event.target;
      if (!target || !target.matches) return;
      if (target.matches('[data-bb-option]')) {
        this.update();
        this.queueTrack(target.getAttribute('data-bb-option'), target.value);
      } else if (target.matches('[data-bb-logo]')) {
        this.update();
        var hasLogo = !!(target.files && target.files.length);
        if (hasLogo !== this.lastLogo) {
          this.lastLogo = hasLogo;
          this.queueTrack('logo', hasLogo ? 'added' : 'removed');
        }
      }
    }

    onInput(event) {
      var target = event.target;
      if (!target || !target.matches) return;
      if (target.matches('[data-bb-text], [data-bb-notes]')) {
        this.update();
        if (target.matches('[data-bb-text]')) {
          var hasText = !!(this.line1 && this.line1.value.trim()) || !!(this.line2 && this.line2.value.trim());
          if (hasText !== this.lastText) {
            this.lastText = hasText;
            this.queueTrack('name_plate', hasText ? 'text added' : 'no text');
          }
        }
      }
    }

    queueTrack(option, value) {
      var self = this;
      window.clearTimeout(this.timers[option]);
      this.timers[option] = window.setTimeout(function () {
        delete self.timers[option];
        track('builder_option_change', { option: option, value: value, value_total: self.state().total / 100, currency: self.config.currency || undefined });
      }, 700);
    }

    /* ----- rendering ----- */
    update() {
      var state = this.state();
      this.paint(state);
      this.renderValues(state);
      this.renderSummary(state);
      this.renderPrices(state);
      this.renderMode(state);
      if (!this.quoteDirty) this.writeQuote(state);
    }

    paint(state) {
      var svg = this.svg;
      if (!svg) return;
      var gold = !!(state.finish.el && state.finish.el.getAttribute('data-bb-gold') === 'true');
      var mm = state.thickness.el ? Number(state.thickness.el.getAttribute('data-bb-mm')) || 2 : 2;
      svg.setAttribute('data-shape', state.shape.value || 'classic');
      svg.setAttribute('data-sides', state.sides.value || '4');
      var buildLabel = String(state.build.label || '').toLowerCase();
      svg.setAttribute('data-metal', /cast|zinc|\bhd\b/.test(buildLabel) ? 'zinc' : 'brass');
      svg.setAttribute('data-finish', gold ? 'gold' : 'standard');
      svg.setAttribute('data-thickness', THICK_DRAW[mm] || '2');
      svg.setAttribute('data-strap', state.strap.value || 'black');
      svg.setAttribute('data-lines', state.line2 ? '2' : (state.line1 ? '1' : '0'));
      var l1 = svg.querySelector('.belt-svg__l1');
      var l2 = svg.querySelector('.belt-svg__l2');
      if (l1 && l1.textContent !== state.line1.toUpperCase()) {
        l1.textContent = state.line1.toUpperCase();
        fitText(l1, TEXT_MAX);
      }
      if (l2 && l2.textContent !== state.line2.toUpperCase()) {
        l2.textContent = state.line2.toUpperCase();
        fitText(l2, TEXT_MAX);
      }
      var words = 'Illustration of a championship belt: ' + (SHAPE_WORDS[state.shape.value] || 'classic') + ' centre plate, ' +
        (state.sides.value || '4') + ' side plates, ' +
        (state.build.label ? state.build.label + ' build, ' : '') + mm + ' mm plates, ' +
        (gold ? '24K gold electroplated finish, ' : 'standard finish, ') +
        (state.strap.value === 'white' ? 'white leather strap' : state.strap.value === 'other' ? 'leather strap in another colour' : 'black leather strap') +
        (state.line1 ? ', name plate reads ' + state.line1 + (state.line2 ? ' ' + state.line2 : '') : '') + '.';
      svg.setAttribute('aria-label', words);
    }

    renderValues(state) {
      var map = {
        shape: state.shape.label, sides: state.sides.label, build: state.build.label, finish: state.finish.label,
        thickness: state.thickness.label, strap: state.strap.label, speed: state.speed.label, qty: state.qtyLabel
      };
      Array.prototype.forEach.call(this.querySelectorAll('[data-bb-value]'), function (node) {
        var key = node.getAttribute('data-bb-value');
        if (map[key] !== undefined && node.textContent !== map[key]) node.textContent = map[key];
      });
    }

    renderSummary(state) {
      if (!this.summary) return;
      var self = this;
      var rows = [
        ['Centre plate', state.shape.label],
        ['Side plates', state.sides.label]
      ];
      if (state.build.label) rows.push(['Build', state.build.label]);
      if (state.finish.label) rows.push(['Finish', state.finish.label]);
      if (state.thickness.label) rows.push(['Plate thickness', state.thickness.label]);
      rows.push(['Strap colour', state.strap.label]);
      if (state.speed.label) rows.push(['Build time', state.speed.label]);
      rows.push(['Quantity', state.qtyLabel]);
      rows.push(['Name plate', this.textLine(state)]);
      rows.push(['Logo', state.file ? state.file.name : 'None']);
      if (state.notes) rows.push(['Notes', state.notes.length > 80 ? state.notes.slice(0, 79) + '…' : state.notes]);
      var fragment = doc.createDocumentFragment();
      rows.forEach(function (item) { fragment.appendChild(row(item[0], item[1])); });
      self.summary.textContent = '';
      self.summary.appendChild(fragment);
    }

    renderPrices(state) {
      var self = this;
      var total = state.quoteOnly ? 'Quoted' : this.money(state.total);
      this.totals.forEach(function (node) {
        if (node.textContent !== total) node.textContent = total;
      });
      if (!this.prices) return;
      var fragment = doc.createDocumentFragment();
      fragment.appendChild(row('Belt', this.money(state.unit)));
      if (state.discount) fragment.appendChild(row('Volume price, ' + state.pct + '% off', '−' + this.money(state.discount)));
      state.addons.forEach(function (choice) {
        fragment.appendChild(row(choice.addon, '+' + self.money(choice.price)));
      });
      if (state.count > 1) {
        fragment.appendChild(row('Per belt', this.money(state.perBelt)));
        fragment.appendChild(row('Quantity', '× ' + state.count));
      }
      fragment.appendChild(row(state.quoteOnly ? 'Total (per belt, before your quote)' : 'Total', state.quoteOnly ? this.money(state.perBelt) : this.money(state.total), 'belt-builder__row--total'));
      Array.prototype.forEach.call(fragment.querySelectorAll('dd'), function (dd) { dd.className = 'price'; });
      this.prices.textContent = '';
      this.prices.appendChild(fragment);
    }

    /* Beyond the quoted quantity, the cart button steps aside and the quote form opens. */
    renderMode(state) {
      if (this.addWrap) this.addWrap.hidden = state.quoteOnly;
      if (this.quoteNote) this.quoteNote.hidden = !state.quoteOnly;
      if (state.quoteOnly && this.quoteBox && !this.quoteBox.open) this.quoteBox.open = true;
    }

    quoteText(state) {
      var lines = [
        'Belt builder request',
        '',
        'Centre plate: ' + state.shape.label,
        'Side plates: ' + state.sides.label
      ];
      if (state.build.label) lines.push('Build: ' + state.build.label + (state.build.diff ? '' : ' (included)'));
      if (state.finish.label) lines.push('Finish: ' + state.finish.label + (state.finish.diff ? '' : ' (included)'));
      if (state.thickness.label) lines.push('Plate thickness: ' + state.thickness.label + (state.thickness.diff ? '' : ' (included)'));
      lines.push('Strap colour: ' + state.strap.label);
      if (state.speed.label) lines.push('Build time: ' + state.speed.label);
      lines.push('Quantity: ' + (state.quoteOnly ? 'more than ' + this.quoteAbove + ' belts (please quote)' : state.qtyLabel));
      lines.push('Name plate, line 1: ' + (state.line1 || '(no text)'));
      if (state.line2) lines.push('Name plate, line 2: ' + state.line2);
      lines.push('Logo: ' + (state.file ? state.file.name + ' (I will attach it when I reply to your email)' : 'none'));
      if (state.notes) lines.push('Notes: ' + state.notes);
      if (this.base.price) {
        lines.push('', state.quoteOnly
          ? 'Price per belt shown in the builder, before volume pricing: ' + this.money(state.perBelt)
          : 'Total shown in the builder: ' + this.money(state.total));
      }
      lines.push('', 'The date I need the belts by: ');
      return lines.join('\n');
    }

    writeQuote(state) {
      if (!this.quoteBody) return;
      var text = this.quoteText(state);
      if (this.quoteBody.value !== text) this.quoteBody.value = text;
    }

    /* ----- quote form ----- */
    setupQuote() {
      var self = this;
      var form = this.quoteForm;
      var key = 'ms_lead_topic:' + form.id;
      var success = form.querySelector('[data-track-success]');
      var message = form.querySelector('[data-form-message]');
      try {
        if (success) {
          var topic = window.sessionStorage.getItem(key);
          if (topic) success.setAttribute('data-topic', topic);
          window.sessionStorage.removeItem(key);
        }
      } catch (error) { /* storage blocked: the lead is still reported, without a topic */ }

      if (message) {
        var box = message.closest('details');
        if (box) box.open = true;
        var focusMessage = function () {
          var active = doc.activeElement;
          if (!active || active === doc.body) message.focus();
        };
        focusMessage();
        window.addEventListener('load', focusMessage);
      }

      form.addEventListener('focusin', function (event) {
        if (self.quoteStarted || !event.target.matches('input, textarea, select')) return;
        self.quoteStarted = true;
        track('quote_form_start', { form: 'builder_quote' });
      });

      form.addEventListener('submit', function () {
        var state = self.state();
        var topic = 'Belt builder / ' + state.shape.label + ', ' + state.sides.label + ' side plates' + (state.quoteOnly ? ', bulk' : '');
        try {
          window.sessionStorage.setItem(key, topic);
        } catch (error) { /* storage blocked */ }
      });
    }

    /* ----- cart ----- */
    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    setBusy(busy) {
      var button = this.addButton;
      if (!button) return;
      if (busy) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      } else {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }

    addToCart(button) {
      var self = this;
      if (this.busy) return;
      var state = this.state();
      if (state.quoteOnly) {
        this.showError('Sets beyond ' + this.quoteAbove + ' belts are priced by quote. Please use the quote form below.');
        return;
      }
      var beltVariant = state.variant ? state.variant.id : this.base.variant;
      if (!beltVariant || typeof window.fetch !== 'function' || typeof window.FormData !== 'function') {
        this.showError('This belt cannot be added to the cart here. Please ask for a quote below.');
        return;
      }
      if (state.variant && state.variant.available === false) {
        this.showError('This combination is not available at the moment. Please ask for a quote below.');
        return;
      }
      var group = groupId();
      var root = rootUrl();
      var items = state.addons.map(function (choice) {
        var properties = { 'For belt': GROUP_TITLE, _ms_group: group };
        if (choice === state.engraving) {
          properties.Engraving = state.line1;
          if (state.line2) properties['Engraving, second line'] = state.line2;
        }
        return { id: choice.variant, quantity: state.count, properties: properties };
      });

      var body = new FormData();
      body.append('id', String(beltVariant));
      body.append('quantity', String(state.count));
      body.append('properties[Build]', this.buildLine(state));
      body.append('properties[Centre plate]', state.shape.label);
      body.append('properties[Side plates]', state.sides.label);
      body.append('properties[Strap colour]', state.strap.label);
      if (state.speed.label) body.append('properties[Build time]', state.speed.label);
      if (state.engraving) {
        body.append('properties[Engraving]', state.line1);
        if (state.line2) body.append('properties[Engraving, second line]', state.line2);
      }
      if (state.notes) body.append('properties[Notes]', state.notes);
      if (state.file) body.append('properties[Logo]', state.file, state.file.name);
      body.append('properties[_ms_group]', group);

      this.busy = true;
      this.setBusy(true);
      this.showError('');
      var addedKeys = [];

      var addAddons = items.length
        ? fetch(root + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(toResult)
        : Promise.resolve({ ok: true, data: { items: [] } });

      addAddons
        .then(function (result) {
          if (!result.ok) throw new Error(result.data.description || result.data.message || 'The upgrades could not be added to your cart.');
          addedKeys = (result.data.items || []).map(function (item) { return item.key; }).filter(Boolean);
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            body: body
          }).then(toResult);
        })
        .then(function (result) {
          if (!result.ok) {
            var message = result.data.description || result.data.message || 'The belt could not be added to your cart.';
            return self.rollback(root, addedKeys).then(function () { throw new Error(message); });
          }
          track('builder_add_to_cart', {
            shape: state.shape.value,
            side_plates: state.sides.value,
            build: state.build.value,
            finish: state.finish.value,
            thickness_mm: state.thickness.el ? state.thickness.el.getAttribute('data-bb-mm') : '',
            strap: state.strap.value,
            build_time: state.speed.value,
            quantity: state.count,
            engraving: state.engraving ? 'yes' : 'no',
            logo: state.file ? 'yes' : 'no',
            value: state.total / 100,
            currency: self.config.currency || undefined
          });
          self.busy = false;
          self.setBusy(false);
          if (window.MS && typeof window.MS.refreshCart === 'function' && typeof window.MS.openCart === 'function') {
            return window.MS.refreshCart().then(function () {
              window.MS.openCart(button, 'builder');
            });
          }
          window.location.href = root + 'cart';
          return null;
        })
        .catch(function (error) {
          self.busy = false;
          self.setBusy(false);
          var text = error && error.message && !/fetch|network/i.test(error.message)
            ? error.message + ' Please try again, or ask for a quote below.'
            : 'This could not be added to your cart. Check your connection and try again.';
          self.showError(text);
        });
    }

    /* If the belt line fails after its upgrades were added, take the upgrades out again. */
    rollback(root, keys) {
      if (!keys.length) return Promise.resolve();
      var updates = {};
      keys.forEach(function (key) { updates[key] = 0; });
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function () {}, function () {});
    }
  }

  /* ---------- <mockup-gallery> ---------- */
  var cloneCount = 0;

  /* Copies a drawing for the lightbox with fresh ids, so the copy never shares gradient ids with the tile. */
  function cloneSvg(svg) {
    cloneCount += 1;
    var suffix = '-lb' + cloneCount;
    var copy = svg.cloneNode(true);
    var ids = {};
    Array.prototype.forEach.call(copy.querySelectorAll('[id]'), function (node) {
      ids[node.id] = node.id + suffix;
      node.id = node.id + suffix;
    });
    Array.prototype.forEach.call(copy.querySelectorAll('*'), function (node) {
      Array.prototype.slice.call(node.attributes).forEach(function (attribute) {
        var value = attribute.value;
        if (value.indexOf('#') === -1) return;
        var next = value.replace(/url\(#([^)]+)\)/g, function (match, key) {
          return ids[key] ? 'url(#' + ids[key] + ')' : match;
        });
        if ((attribute.name === 'href' || attribute.name === 'xlink:href') && next.charAt(0) === '#' && ids[next.slice(1)]) {
          next = '#' + ids[next.slice(1)];
        }
        if (next !== value) node.setAttribute(attribute.name, next);
      });
    });
    return copy;
  }

  class MockupGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var self = this;
      this.dialog = this.querySelector('[data-mg-dialog]');
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-mg-item]'));
      this.index = 0;

      var drawings = Array.prototype.slice.call(this.querySelectorAll('.belt-svg'));
      drawings.forEach(fitDrawing);
      whenFontsReady(function () { drawings.forEach(fitDrawing); });

      if (!this.dialog || !this.items.length) return;
      this.target = this.dialog.querySelector('[data-mg-target]');
      this.caption = this.dialog.querySelector('[data-mg-caption]');
      this.count = this.dialog.querySelector('[data-mg-count]');
      this.prev = this.dialog.querySelector('[data-mg-prev]');
      this.next = this.dialog.querySelector('[data-mg-next]');
      if (this.items.length < 2) {
        if (this.prev) this.prev.hidden = true;
        if (this.next) this.next.hidden = true;
      }

      this.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var opener = target.closest('[data-mg-open]');
        if (opener && self.contains(opener)) {
          var item = opener.closest('[data-mg-item]');
          self.open(self.items.indexOf(item), opener);
          return;
        }
        if (target.closest('[data-mg-close]')) {
          self.close();
          return;
        }
        if (target.closest('[data-mg-prev]')) self.show(self.index - 1);
        if (target.closest('[data-mg-next]')) self.show(self.index + 1);
      });

      /* A click on the backdrop lands on the dialog itself, outside its box. */
      this.dialog.addEventListener('click', function (event) {
        if (event.target !== self.dialog) return;
        var box = self.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) self.close();
      });

      this.dialog.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { event.preventDefault(); self.show(self.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); self.show(self.index + 1); }
      });

      this.dialog.addEventListener('close', function () {
        if (self.target) self.target.textContent = '';
      });
    }

    show(index) {
      var count = this.items.length;
      if (!count) return;
      this.index = (index + count) % count;
      var item = this.items[this.index];
      var media = item.querySelector('[data-mg-media]');
      var label = item.querySelector('[data-mg-label]');
      var caption = item.querySelector('[data-mg-caption-text]');
      this.target.textContent = '';
      if (media) {
        var svg = media.querySelector('svg.belt-svg');
        var img = media.querySelector('img');
        if (svg) {
          var copy = cloneSvg(svg);
          this.target.appendChild(copy);
          fitDrawing(copy);
        } else if (img) {
          var picture = img.cloneNode(true);
          picture.setAttribute('sizes', '(min-width: 80rem) 76rem, 100vw');
          picture.setAttribute('loading', 'eager');
          picture.removeAttribute('fetchpriority');
          this.target.appendChild(picture);
        }
      }
      if (label) this.target.appendChild(element('span', 'media-label', label.textContent));
      if (this.caption) this.caption.textContent = caption ? caption.textContent : '';
      if (this.count) this.count.textContent = count > 1 ? (this.index + 1) + ' of ' + count : '';
      this.dialog.setAttribute('aria-label', (caption && caption.textContent.trim()) || 'Mockup');
    }

    open(index, opener) {
      if (index < 0) return;
      this.show(index);
      if (window.MS && typeof window.MS.openDialog === 'function') {
        window.MS.openDialog(this.dialog, opener);
      } else if (typeof this.dialog.showModal === 'function') {
        this.opener = opener;
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      track('mockup_view', { position: this.index + 1 });
    }

    close() {
      if (window.MS && typeof window.MS.closeDialog === 'function') {
        window.MS.closeDialog(this.dialog);
        return;
      }
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }
  }

  if (window.customElements) {
    if (!window.customElements.get('belt-builder')) window.customElements.define('belt-builder', BeltBuilder);
    if (!window.customElements.get('mockup-gallery')) window.customElements.define('mockup-gallery', MockupGallery);
  }
})();
